/**
 * The raids.
 *
 * Every half hour the orcs come. That is the shape of the game: build, arm,
 * hold, and eventually go across and break the lairs so they stop coming.
 *
 * A clock and nothing else. It does not know what an orc is or how one is
 * drawn - it says when, and how many, and the caller does the rest.
 *
 * Real time rather than game time, because the player asked for half an hour
 * and half an hour of a twenty-minute day is one and a half days, which lands
 * at a different hour every time. The countdown is on the HUD for exactly that
 * reason: a raid you cannot see coming is a raid you cannot prepare for, and
 * the sky cannot tell you when this one is due.
 */

/** Seconds between raids. */
export const RAID_INTERVAL = 30 * 60;
/** How long before one lands the horn sounds. */
export const RAID_WARNING = 60;
/** How many each lair sends, and how much that grows with every wave. */
export const ORCS_PER_LAIR = 3;
export const ORCS_PER_LAIR_PER_WAVE = 0.5;

/**
 * The most orcs that may be alive at once.
 *
 * Raids add and only the player takes away, so without a ceiling the number
 * climbs for as long as the game is open: measured at 175 orcs after two
 * hours of nobody fighting back, 515 after four and 1675 after eight. Each
 * one is seven draw calls and a body pathing every tick, and on a machine
 * whose graphics memory is the system memory that is how a game takes the
 * whole screen down with it.
 *
 * Forty is more than enough to be a raid - five lairs sending eight each -
 * and a wave that would go over the top simply arrives smaller. The horn
 * still sounds, the raid still comes, and a player who has let the island
 * fill up gets no more until they thin it out. Which is the right pressure
 * anyway: standing and fighting should be how a raid ends.
 */
export const MAX_ORCS = 40;

/**
 * Hardcore: twice the orcs, and the first wave five minutes after the peace.
 *
 * "Кількість орків збільшиться вдвічі - кількість спавнерів збільшувати не
 * обов'язково, просто більший ліміт на максимум орків. Орки починають атаку
 * через 5 хвилин після старту."
 *
 * The ceiling rather than the lairs, exactly as asked: the same five лігва
 * send the same waves, and what changes is how many of them the island will
 * hold at once. Eighty is the number the frame rate was measured against -
 * see the note above - and it is a hard hour on a machine like this one.
 */
export const HARDCORE_MAX_ORCS = MAX_ORCS * 2;

/**
 * Ten quiet minutes before any of that starts.
 *
 * "Додай 10 хвилин затримки на карті кремль, перш ніж орки можуть атакувати,
 * коли вибрано хардкор." Time to build something before the march begins:
 * nobody walks on your island until this has run out, and the first wave
 * keeps its old five minutes, counted from the end of it.
 */
export const HARDCORE_PEACE = 10 * 60;
export const HARDCORE_FIRST_RAID = HARDCORE_PEACE + 5 * 60;

export type RaidEvent = "none" | "warning" | "raid";

export class RaidClock {
  /** Seconds until the next one lands. */
  private left: number;
  /** How many have already come. */
  wave = 0;
  private warned = false;

  /**
   * @param first seconds until the *first* wave, which is not always the
   * interval: hardcore opens with one fifteen minutes in. Every wave after it
   * is `RAID_INTERVAL` apart whatever this was.
   */
  constructor(first = RAID_INTERVAL) {
    this.left = first;
  }

  get secondsLeft(): number {
    return this.left;
  }

  /** How many orcs one lair sends this time. Later waves are heavier. */
  get perLair(): number {
    return Math.floor(ORCS_PER_LAIR + this.wave * ORCS_PER_LAIR_PER_WAVE);
  }

  /**
   * How many actually come, given how many are already about.
   *
   * The wave a raid *wants* to send, trimmed to what the field will hold.
   * Never negative: an island already over the ceiling gets nobody new.
   */
  waveSize(lairs: number, alreadyAlive: number, cap = MAX_ORCS): number {
    return Math.max(0, Math.min(this.perLair * lairs, cap - alreadyAlive));
  }

  /**
   * Run the clock.
   *
   * `lairs` is how many are still standing: with none left there is nothing to
   * count down to, and the clock stops rather than firing empty raids for ever.
   */
  step(dt: number, lairs: number): RaidEvent {
    if (lairs <= 0) return "none";

    this.left -= dt;

    if (this.left <= 0) {
      this.left = RAID_INTERVAL;
      this.warned = false;
      this.wave += 1;
      return "raid";
    }

    if (!this.warned && this.left <= RAID_WARNING) {
      this.warned = true;
      return "warning";
    }
    return "none";
  }

  /** mm:ss, for the HUD. */
  format(): string {
    const total = Math.max(0, Math.ceil(this.left));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }
}

/**
 * Лігво, that keeps sending.
 *
 * The raids are still the half-hourly event they always were; this is the
 * slow drip in between. Every lair puts one orc out every so often and stops
 * once it has five of its own alive - so a fortress left alone fills up to a
 * garrison and no further, and a player who clears a room keeps it clear for
 * a few minutes rather than forever.
 *
 * The ceiling is per lair and not per world on purpose: it is what makes
 * breaking one worth doing. Take a lair out and the five it was feeding stop
 * being replaced, whatever the rest of the fortress is doing.
 */
export const SPAWN_INTERVAL = 5 * 60;
export const SPAWN_PER_LAIR = 5;

export interface LairSpawn {
  /** Which lair, by its cell key. */
  readonly at: { x: number; y: number; z: number };
}

/**
 * Whose turn it is to send one.
 *
 * Pure: it is handed how long has passed, where the lairs are and how many
 * each already has alive, and it says which of them may put another out. What
 * an orc *is* stays the caller's business.
 */
export class LairClock {
  private since = 0;

  /**
   * @param alive how many live orcs belong to the lair at that cell key.
   * @returns the lairs that may send one now.
   */
  step(
    dt: number,
    lairs: readonly { x: number; y: number; z: number }[],
    alive: (lair: { x: number; y: number; z: number }) => number,
  ): LairSpawn[] {
    this.since += dt;
    if (this.since < SPAWN_INTERVAL) return [];
    this.since -= SPAWN_INTERVAL;
    const out: LairSpawn[] = [];
    for (const lair of lairs) {
      if (alive(lair) >= SPAWN_PER_LAIR) continue;
      out.push({ at: lair });
    }
    return out;
  }

  /** Seconds until the next one is due. For the HUD, and for tests. */
  get left(): number {
    return Math.max(0, SPAWN_INTERVAL - this.since);
  }
}
